import { ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";

import { AUTH_TYPE, AuthOptions, createAuthLink } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { fetchAuthSession } from "aws-amplify/auth";

import Toast from "@covertree/utils/Toast";

const getJWTToken = async () => {
  const jwtToken = (await fetchAuthSession())?.tokens?.idToken?.toString();
  if (!jwtToken) {
    Toast.showError("Your session has expired. Please log in again.");

    // Note: Delay the redirect to allow the error message to be displayed
    setTimeout(() => window.location.replace("/"), 1500);

    throw new Error("No JWT token found");
  }
  return jwtToken;
};

const getIamCredentials = async () => {
  return (await fetchAuthSession()).credentials!;
};

const createApolloLink = (authType: AUTH_TYPE): ApolloLink => {
  if (
    ![
      AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
      AUTH_TYPE.API_KEY,
      AUTH_TYPE.AWS_IAM,
    ].includes(authType)
  ) {
    throw new Error("Invalid auth type");
  }

  const auth: AuthOptions = (() => {
    if (authType === AUTH_TYPE.AMAZON_COGNITO_USER_POOLS) {
      return {
        type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
        jwtToken: getJWTToken,
      };
    }

    if (authType === AUTH_TYPE.API_KEY) {
      return {
        apiKey: process.env.VITE_AWS_GRAPHQL_API_KEY!,
        type: AUTH_TYPE.API_KEY,
      };
    }

    return {
      type: AUTH_TYPE.AWS_IAM,
      credentials: getIamCredentials,
    };
  })();

  const config = {
    url: process.env.VITE_AWS_GRAPHQL_API || "",
    region: process.env.VITE_AWS_COGNITO_REGION || "",
    auth,
  };

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        // eslint-disable-next-line no-console
        console.warn(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`
        )
      );
    // eslint-disable-next-line no-console
    if (networkError) console.warn(`[Network error]: ${networkError}`);
  });

  const removeTypenameLink = removeTypenameFromVariables();

  return ApolloLink.from([
    removeTypenameLink,
    errorLink,
    createAuthLink(config),
    createSubscriptionHandshakeLink(config),
  ]);
};

export default createApolloLink;
